import React, { useEffect, useState } from 'react';
import styled, { keyframes } from 'styled-components';
import { RequestState } from '../../auth/hooks/useRequestState';
import { COLORS } from '../settings/variables/Colors';
import { SPACERS } from '../settings/variables/Spacers';

const Z_INDEX = {
  modal: 1000,
};

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
`;

const fadeOut = keyframes`
  from { opacity: 1; transform: translateY(0); }
  to { opacity: 0; transform: translateY(-20px); }
`;

const StyledContainer = styled.div<{
  show: boolean;
  type: 'loading' | 'success' | 'error';
}>`
  position: fixed;
  bottom: ${SPACERS.large};
  left: ${SPACERS.large};
  background: ${({ type }) =>
    type === 'success'
      ? COLORS.success
      : type === 'error'
      ? COLORS.critical
      : COLORS.ctaPrimary};
  color: white;
  padding: ${SPACERS.medium} ${SPACERS.large};
  border-radius: 8px;
  display: flex;
  align-items: center;
  gap: ${SPACERS.medium};
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  z-index: ${Z_INDEX.modal + 1};
  animation: ${({ show }) => (show ? fadeIn : fadeOut)} 0.3s ease-out forwards;
`;

const IconContainer = styled.div`
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.2);
`;

const Checkmark = styled.div`
  width: 16px;
  height: 16px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;

  &::after {
    content: '';
    position: absolute;
    width: 5px;
    height: 10px;
    border: solid white;
    border-width: 0 2px 2px 0;
    transform: rotate(45deg);
    margin-top: -2px;
  }
`;

const Cross = styled.div`
  width: 16px;
  height: 16px;
  position: relative;

  &::before,
  &::after {
    content: '';
    position: absolute;
    width: 16px;
    height: 2px;
    background: white;
    top: 50%;
    left: 50%;
  }

  &::before {
    transform: translate(-50%, -50%) rotate(45deg);
  }

  &::after {
    transform: translate(-50%, -50%) rotate(-45deg);
  }
`;

const Spinner = styled.div`
  width: 16px;
  height: 16px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-radius: 50%;
  border-top-color: white;
  animation: spin 1s ease-in-out infinite;

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

const Message = styled.div`
  font-size: 14px;
  font-weight: 500;
`;

export type FloatMessageType = 'loading' | 'success' | 'error';

interface TrueImpactFloatMessageProps {
  message?: string | null;
  type: FloatMessageType;
  show?: boolean;
}

const ICONS: Record<FloatMessageType, React.ReactNode> = {
  loading: <Spinner />,
  success: <Checkmark />,
  error: <Cross />,
};

export const TrueImpactFloatMessage: React.FC<TrueImpactFloatMessageProps> = ({
  message,
  type,
  show,
}) => {
  const visible = show ?? Boolean(message);
  if (!visible || !message) return null;
  return (
    <StyledContainer show={visible} type={type}>
      <IconContainer>{ICONS[type]}</IconContainer>
      <Message>{message}</Message>
    </StyledContainer>
  );
};

interface TrueImpactFloatLoadingProps {
  requestState: RequestState<unknown>;
  loadingMessage?: string;
  successMessage?: string;
  errorMessage?: string;
}

const TrueImpactFloatLoading: React.FC<TrueImpactFloatLoadingProps> = ({
  requestState,
  loadingMessage = 'Loading...',
  successMessage = 'Operation completed successfully!',
  errorMessage = 'An error occurred. Please try again.',
}) => {
  const [show, setShow] = useState(false);
  const isLoading = requestState.type === 'REQUEST_START';
  const isSuccess = requestState.type === 'REQUEST_SUCCESS';
  const isError = requestState.type === 'REQUEST_ERROR';

  useEffect(() => {
    if (isLoading || isSuccess || isError) {
      setShow(true);
      if (!isLoading) {
        const timer = setTimeout(() => {
          setShow(false);
        }, 3000);
        return () => clearTimeout(timer);
      }
    }
  }, [isLoading, isSuccess, isError]);

  if (!show) return null;

  const type: FloatMessageType = isSuccess
    ? 'success'
    : isError
    ? 'error'
    : 'loading';
  const message = isSuccess
    ? successMessage
    : isError
    ? requestState.error?.message || errorMessage
    : loadingMessage;

  return <TrueImpactFloatMessage show={show} type={type} message={message} />;
};

export default TrueImpactFloatLoading;
